import {
  CheckSquare,
  Square,
  Plus,
  Trash,
  Package,
  ClipboardText,
  Lightning,
  Warning,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/DateField";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CHECKLIST_DEFAULTS = [
  ["Quote accepted", "quote_accepted"],
  ["Deposit paid", "deposit_paid"],
  ["Template booked", "template_booked"],
  ["Slab selected & tagged", "slab_tagged"],
  ["Sink & faucet cutouts confirmed", ""],
  ["Edge profile confirmed", ""],
  ["Drawing approved", "drawing_approved"],
  ["Material received", "material_received"],
  ["Plumber / electrician scheduled", ""],
  ["Old top removal confirmed", ""],
  ["Install crew assigned", "install_crew"],
  ["Installed", "installed"],
  ["Paid in full", "paid_in_full"],
];

// Facts the backend evaluates to tick items automatically (projects.py auto_tick_checklist)
export const CHECKLIST_RULES = [
  ["", "Manual"],
  ["quote_accepted", "Quote accepted"],
  ["deposit_paid", "Deposit paid"],
  ["drawing_approved", "Drawing approved"],
  ["template_booked", "Template booked"],
  ["templated", "Template done"],
  ["slab_tagged", "Slab reserved"],
  ["material_received", "Material received (PO in)"],
  ["install_booked", "Install booked"],
  ["install_crew", "Install crew assigned"],
  ["installed", "Installed"],
  ["paid_in_full", "Paid in full"],
];

const SO_STATUS = ["Ordered", "In transit", "Received", "Attention"];

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : String(Date.now()) + Math.random().toString(16).slice(2);

const Panel = ({ title, icon: Icon, action, children, testid }) => (
  <div
    className="rounded-lg border border-zinc-800 bg-zinc-900"
    data-testid={testid}
  >
    <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
      <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-300">
        {Icon && <Icon size={16} weight="bold" className="text-indigo-300" />}
        {title}
      </span>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

export const checklistItems = (p) =>
  p.checklist && Array.isArray(p.checklist.items) && p.checklist.items.length
    ? p.checklist.items
    : CHECKLIST_DEFAULTS.map(([label, auto], i) => ({
        id: `default-${i}`,
        label,
        done: false,
        auto,
      }));

const Checklist = ({ p, patch }) => {
  const items = checklistItems(p);
  const facts = p.checklist?.facts || {};
  const save = (next) => patch({ checklist: { items: next } }, true);
  const toggle = (id) =>
    save(
      items.map((it) =>
        it.id === id
          ? { ...it, done: !it.done, done_by: "manual", done_at: !it.done ? new Date().toISOString() : "" }
          : it,
      ),
    );
  const setRule = (id, auto) =>
    save(
      items.map((it) =>
        it.id === id
          ? { ...it, auto, ...(it.done_by === "auto" && !auto ? { done: false, done_by: "", done_at: "" } : {}), ...(it.done_by === "manual" && !it.done ? { done_by: "" } : {}) }
          : it,
      ),
    );
  const remove = (id) => save(items.filter((it) => it.id !== id));
  const add = () => {
    const label = window.prompt("Checklist item");
    if (label) save([...items, { id: uid(), label, done: false, auto: "" }]);
  };
  const done = items.filter((it) => it.done).length;
  const autoCount = items.filter((it) => it.auto).length;
  return (
    <Panel
      title={`Checklist ${done}/${items.length}`}
      icon={ClipboardText}
      testid="project-checklist"
      action={
        <Button
          data-testid="checklist-add"
          variant="outline"
          onClick={add}
          className="h-8 border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest"
        >
          <Plus size={13} weight="bold" className="mr-1" /> Add
        </Button>
      }
    >
      {autoCount > 0 && (
        <p
          className="mb-3 flex items-center gap-1.5 text-[11px] text-zinc-500"
          data-testid="checklist-auto-hint"
        >
          <Lightning size={12} weight="fill" className="text-amber-300" />
          {autoCount} item{autoCount === 1 ? "" : "s"} tick automatically when the matching job, PO or payment is done.
        </p>
      )}
      <div className="space-y-1.5">
        {items.map((it) => (
          <div
            key={it.id}
            data-testid={`checklist-item-${it.id}`}
            className="flex items-center gap-2.5 rounded-lg px-2 py-2 transition-colors duration-150 hover:bg-zinc-800/60"
          >
            <button
              type="button"
              data-testid={`checklist-toggle-${it.id}`}
              onClick={() => toggle(it.id)}
              className="flex flex-1 items-center gap-2.5 text-left text-sm"
            >
              {it.done ? (
                <CheckSquare
                  size={16}
                  weight="fill"
                  className="text-emerald-400"
                />
              ) : (
                <Square size={16} weight="bold" className="text-zinc-600" />
              )}
              <span
                className={
                  it.done ? "text-zinc-500 line-through" : "text-zinc-200"
                }
              >
                {it.label}
              </span>
              {it.done && it.done_by === "auto" && (
                <span
                  data-testid={`checklist-auto-done-${it.id}`}
                  title={`Ticked automatically${it.done_at ? ` · ${new Date(it.done_at).toLocaleDateString()}` : ""}`}
                  className="flex items-center gap-1 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-amber-300"
                >
                  <Lightning size={9} weight="fill" /> Auto
                </span>
              )}
              {!it.done && it.auto && facts[it.auto] === false && (
                <span className="text-[10px] text-zinc-600">
                  · waiting
                </span>
              )}
            </button>
            <Select value={it.auto || ""} onValueChange={(v) => setRule(it.id, v === "manual" ? "" : v)}>
              <SelectTrigger
                data-testid={`checklist-rule-${it.id}`}
                title="Tick this item automatically when…"
                className={`h-7 w-40 border-zinc-800 bg-zinc-950 text-[10px] ${
                  it.auto ? "text-amber-200" : "text-zinc-500"
                }`}
              >
                <Lightning size={10} weight={it.auto ? "fill" : "bold"} className="mr-1 shrink-0" />
                <SelectValue placeholder="Manual" />
              </SelectTrigger>
              <SelectContent>
                {CHECKLIST_RULES.map(([v, label]) => (
                  <SelectItem key={v || "manual"} value={v || "manual"} className="text-xs">
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              data-testid={`checklist-remove-${it.id}`}
              onClick={() => remove(it.id)}
              title="Remove"
              className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-600 hover:text-red-400"
            >
              <Trash size={13} weight="bold" />
            </button>
          </div>
        ))}
      </div>
    </Panel>
  );
};

const SpecialOrders = ({ p, patch }) => {
  const rows = p.special_orders || [];
  const save = (next) => patch({ special_orders: next }, true);
  const add = () =>
    save([
      ...rows,
      { id: uid(), item: "", vendor: "", eta: "", status: "Ordered", note: "" },
    ]);
  const upd = (id, field, val) =>
    save(rows.map((r) => (r.id === id ? { ...r, [field]: val } : r)));
  const remove = (id) => save(rows.filter((r) => r.id !== id));
  const backordered = (p.fixtures || []).filter((f) => f.sink_detail?.backorder);
  const addReorder = (f) => {
    const d = f.sink_detail || {};
    save([
      ...rows,
      {
        id: uid(),
        item: d.name || f.sink || "Sink",
        vendor: "",
        eta: "",
        status: "Attention",
        note: `Backordered — reserved out of stock for ${f.area || "fixture"}${d.location ? ` · loc ${d.location}` : ""}`,
      },
    ]);
  };
  const createPO = async (f) => {
    try {
      const { data } = await api.post(`/sinks/${f.sink_id}/reorder-po`, {
        project_id: p.id,
        customer_id: p.customer_id || null,
        customer_name: p.customer_name || "",
        qty: 1,
        note: `Backorder reorder for ${f.area || "fixture"} · ${p.code || ""}`,
      });
      toast.success(`Draft ${data.code} created for ${data.supplier_name || "supplier"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the PO");
    }
  };
  return (
    <div className="space-y-4">
      {backordered.length > 0 && (
        <div
          data-testid="backorder-alert"
          className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4"
        >
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-300">
            <Warning size={14} weight="fill" /> Backordered materials ({backordered.length})
          </div>
          <p className="mt-1 text-xs text-amber-200/70">
            These sinks were reserved while out of stock — reorder to cover the hold.
          </p>
          <div className="mt-3 space-y-2">
            {backordered.map((f) => (
              <div
                key={f.id}
                data-testid={`backorder-row-${f.id}`}
                className="flex flex-wrap items-center gap-3 rounded-md border border-amber-500/30 bg-zinc-950 p-3"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-zinc-100">
                    {f.sink_detail?.name || f.sink || "Sink"}
                  </span>
                  <span className="block text-xs text-zinc-400">
                    {f.area || "Fixture"}
                    {f.sink_detail?.location ? ` · ${f.sink_detail.location}` : ""} · on hand{" "}
                    {f.sink_detail?.quantity}
                  </span>
                </span>
                <Button
                  data-testid={`backorder-reorder-${f.id}`}
                  variant="outline"
                  onClick={() => addReorder(f)}
                  className="h-8 border-amber-500/40 bg-transparent text-[10px] font-bold uppercase tracking-widest text-amber-200 hover:bg-amber-500/10"
                >
                  Add to reorder
                </Button>
                {f.sink_id && (
                  <Button
                    data-testid={`backorder-po-${f.id}`}
                    onClick={() => createPO(f)}
                    className="h-8 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                  >
                    Create PO
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    <Panel
      title={`Special orders (${rows.length})`}
      icon={Package}
      testid="project-special-orders"
      action={
        <Button
          data-testid="special-add"
          variant="outline"
          onClick={add}
          className="h-8 border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest"
        >
          <Plus size={13} weight="bold" className="mr-1" /> Add
        </Button>
      }
    >
      {rows.length === 0 ? (
        <p className="text-xs text-zinc-500">
          No special orders — add sinks, faucets, or vendor items to track.
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div
              key={r.id}
              data-testid={`special-row-${r.id}`}
              className="grid gap-2 rounded-lg border border-zinc-800 bg-zinc-950 p-3 sm:grid-cols-[1fr_1fr_150px_150px_auto]"
            >
              <Input
                data-testid={`special-item-${r.id}`}
                defaultValue={r.item}
                placeholder="Item"
                onBlur={(e) => {
                  if (e.target.value !== r.item)
                    upd(r.id, "item", e.target.value);
                }}
                className="h-9 border-zinc-800 bg-zinc-900 text-sm"
              />
              <Input
                data-testid={`special-vendor-${r.id}`}
                defaultValue={r.vendor}
                placeholder="Vendor"
                onBlur={(e) => {
                  if (e.target.value !== r.vendor)
                    upd(r.id, "vendor", e.target.value);
                }}
                className="h-9 border-zinc-800 bg-zinc-900 text-sm"
              />
              <DateField
                testid={`special-eta-${r.id}`}
                value={r.eta || ""}
                placeholder="ETA"
                onChange={(v) => upd(r.id, "eta", v)}
              />
              <Select
                value={r.status || "Ordered"}
                onValueChange={(v) => upd(r.id, "status", v)}
              >
                <SelectTrigger
                  data-testid={`special-status-${r.id}`}
                  className="h-9 border-zinc-800 bg-zinc-900 text-xs"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SO_STATUS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                type="button"
                data-testid={`special-remove-${r.id}`}
                onClick={() => remove(r.id)}
                title="Remove"
                className="flex h-9 w-9 items-center justify-center rounded-md border border-zinc-800 text-red-400 hover:bg-red-500/10"
              >
                <Trash size={14} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}
    </Panel>
    </div>
  );
};

export const ProjectExtraTabs = ({ p, patch, tab }) => {
  if (tab === "checklist") return <Checklist p={p} patch={patch} />;
  if (tab === "special") return <SpecialOrders p={p} patch={patch} />;
  return null;
};
